import React, {
	useImperativeHandle,
	useState,
	useRef,
	useEffect,
	MutableRefObject,
	CSSProperties,
	forwardRef,
	useCallback
} from "react";
import "./Filters.css";
import Dropdown from "../../shared/dropdown/dropdown";
import Polygon from "@arcgis/core/geometry/Polygon";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Extent from "@arcgis/core/geometry/Extent";

import Radius from "../../radius/Radius";

type FiltersHandle = {
	reset: () => void;
	getFiltersSnapshot: () => { [key: string]: Set<string> };
}

interface iProps {
	view: React.MutableRefObject<__esri.MapView>;
	filterByMapExtent: boolean;
	toggleFilterByMapExtent: Function;
	isSmallScreen: boolean;
	setStateFilterGeometry: React.Dispatch<React.SetStateAction<__esri.Polygon | null>>;
	setRadiusFilterGeometry: React.Dispatch<React.SetStateAction<__esri.Polygon | null>>;
	extent: Extent;
	visible: boolean;
	stateOptions: { value: string, label: string }[];
	stateGeometries: MutableRefObject<{ [key: string]: __esri.Geometry }>;
	classIOptions: string[];
	showMap: () => void;
};

const highlightLayerId: string = 'StateFilterHighlight';
const radiusSketchLayerId: string = "RadiusSketch";

export const TransportationModeOptions = [
	{
		label: "Truck",
		field: "Truck"
	},
	{
		label: "Rail",
		field: "Rail"
	},
	{
		label: "Barge",
		field: "Barge"
	},
	{
		label: "Vessel",
		field: "Vessel"
	},
];

export const StorageOptions = [
	{
		label: "Indoor",
		field: "Indoor_Storage"
	},
	{
		label: "Outdoor",
		field: "Outdoor_Storage"
	},
];

export const CommodityOptions = [
	{
		label: "Liquid",
		field: 'Liquids'
	},
	{
		label: "Bulk",
		field: 'Bulk_'
	},
	{
		label: "Break Bulk",
		field: 'Break_Bulk'
	},
	{
		label: "Project Cargo",
		field: 'Project_Cargo'
	}
];

const Filters: React.ForwardRefRenderFunction<FiltersHandle, iProps> = (props, ref) => {
	useImperativeHandle(ref, () => ({
		reset,
		getFiltersSnapshot
	}));

	const {
		setStateFilterGeometry,
		setRadiusFilterGeometry
	} = props;

	const filtersInitialzed = useRef(false);

	const [selectedStates, setSelectedStates] = useState<Set<string>>(new Set<string>());
	const [selectedClassIOptions, setSelectedClassIOptions] = useState<Set<string>>(new Set<string>());
	const [selectedTransportationModeOptions, setSelectedTransportationModeOptions] = useState<Set<string>>(new Set<string>());
	const [selectedStorageOptions, setSelectedStorageOptions] = useState<Set<string>>(new Set<string>());
	const [selectedCommodityOptions, setSelectedCommodityOptions] = useState<Set<string>>(new Set<string>());

	const [radiusCenterPoint, setRadiusCenterPoint] = useState<__esri.Point | undefined>();
	const [radiusSearchText, setRadiusSearchText] = useState('');

	const [classIDefinitionExpression, setClassIDefinitionExpression] = useState('');
	const [transportationModeDefinitionExpression, setTransportationModeDefinitionExpression] = useState('');
	const [storageDefinitionExpression, setStorageDefinitionExpression] = useState('');
	const [commodityDefinitionExpression, setCommodityDefinitionExpression] = useState('');

	const transloadLayer: MutableRefObject<__esri.FeatureLayer | undefined> = useRef();

	const highlightLayer: MutableRefObject<__esri.GraphicsLayer> = useRef(
		new GraphicsLayer({
			id: highlightLayerId,
			listMode: 'hide'
		})
	);
	const highlightSymbol: MutableRefObject<__esri.SimpleFillSymbol> = useRef(
		new SimpleFillSymbol({
			style: "none",
			outline: {
				color: '#327B8F',
				width: 2,
				style: 'solid'
			}
		})
	);
	const radiusSketchLayer = useRef<__esri.GraphicsLayer>(
		new GraphicsLayer({
			id: radiusSketchLayerId,
			listMode: 'hide'
		})
	);


	const initFilters = () => {
		props.view.current.when(() => {
			if (!filtersInitialzed.current) {
				filtersInitialzed.current = true;

				transloadLayer.current = props.view.current.map.layers
					.find(table => table.title === 'Terminal Points') as __esri.FeatureLayer;

				if (!props.view.current.map.findLayerById(highlightLayerId)) {
					props.view.current.map.add(highlightLayer.current);
				}

				if (!props.view.current.map.findLayerById(radiusSketchLayerId)) {
					props.view.current.map.add(radiusSketchLayer.current);
				}
			}


		});
	}
	useEffect(initFilters, [props.view])

	const onStateCheckboxChange = (evt: any) => {
		const states = new Set(selectedStates);
		if (evt.target.checked) {
			states.add(evt.target.value);
		} else {
			states.delete(evt.target.value);
		}
		setSelectedStates(states);
	}

	const onClassICheckboxChange = (evt: any) => {
		const classIRRs = new Set(selectedClassIOptions);
		if (evt.target.checked) {
			classIRRs.add(evt.target.value);
		} else {
			classIRRs.delete(evt.target.value);
		}
		setSelectedClassIOptions(classIRRs);
	}

	const onTransportationModeCheckboxChange = (evt: any) => {
		const options = new Set(selectedTransportationModeOptions);
		if (evt.target.checked) {
			options.add(evt.target.value);
		} else {
			options.delete(evt.target.value);
		}
		setSelectedTransportationModeOptions(options);
	}

	const onStorageCheckboxChange = (evt: any) => {
		const options = new Set(selectedStorageOptions);
		if (evt.target.checked) {
			options.add(evt.target.value);
		} else {
			options.delete(evt.target.value);
		}
		setSelectedStorageOptions(options);
	}

	const onCommodityCheckboxChange = (evt: any) => {
		const options = new Set(selectedCommodityOptions);
		if (evt.target.checked) {
			options.add(evt.target.value);
		} else {
			options.delete(evt.target.value);
		}
		setSelectedCommodityOptions(options);
	}

	const reset = () => {
		clearAllFilters();
		if (props.view?.current) {
			props.view.current.goTo({
				target: props.extent
			});
		}
		if (!props.filterByMapExtent) {
			props.toggleFilterByMapExtent();
		}
	}

	const clearAllFilters = () => {
		clearStateFilters();
		clearClassIFilters();
		clearRadiusFilter();
		clearTransportationModeFilters();
		clearStorageFilters();
		clearCommodityFilters();
	}

	const clearStateFilters = () => {
		setSelectedStates(new Set());
	}

	const clearClassIFilters = () => {
		setSelectedClassIOptions(new Set());
	}

	const clearRadiusFilter = () => {
		radiusSketchLayer.current?.removeAll();
		setRadiusCenterPoint(undefined);
		setRadiusFilterGeometry(null);
		setRadiusSearchText('');
	}

	const clearTransportationModeFilters = () => {
		setSelectedTransportationModeOptions(new Set());
	}

	const clearStorageFilters = () => {
		setSelectedStorageOptions(new Set());
	}

	const clearCommodityFilters = () => {
		setSelectedCommodityOptions(new Set());
	}

	const selectAllClassIFilters = () => {
		setSelectedClassIOptions(new Set(props.classIOptions));
	}

	const buildStateFilterGeometry = () => {
		if (!transloadLayer.current) {
			return;
		}

		if (selectedStates.size > 0) {
			var geometries = Array.from(selectedStates).map(state => props.stateGeometries.current[state]);
			if (geometries.length > 0) {

				var aggregatedPolygon = new Polygon({
					hasM: false,
					hasZ: false,
					spatialReference: geometries.at(0)?.spatialReference
				});

				geometries.forEach(geometry => {
					if (geometry && geometry.type === 'polygon') {
						const polygon = geometry as __esri.Polygon;
						polygon.rings.forEach(ring => {
							aggregatedPolygon.addRing(ring);
						});
					}
				});

				// push filter geom up to parent
				setStateFilterGeometry(aggregatedPolygon);
			}
		} else {
			setStateFilterGeometry(null);
		}
	}
	useEffect(buildStateFilterGeometry, [selectedStates, setStateFilterGeometry, props.stateGeometries]);

	const highlightSelectedStates = () => {
		highlightLayer.current.removeAll();
		const graphics = Array.from(selectedStates).map(state => new Graphic({ geometry: props.stateGeometries.current[state], symbol: highlightSymbol.current }));
		highlightLayer.current.addMany(graphics);
	}
	useEffect(highlightSelectedStates, [selectedStates, props.stateGeometries]);

	const generateClassIDefinitionExpression = () => {
		let definitionExpression = '';
		if (selectedClassIOptions.size > 0) {
			const formattedClassIOptions = Array.from(selectedClassIOptions).map(x => `'${x.toLowerCase()}'`).join(',');
			definitionExpression = `LOWER(Class_1_Code) IN (${formattedClassIOptions})`;
		}
		setClassIDefinitionExpression(definitionExpression);
	}
	useEffect(generateClassIDefinitionExpression, [selectedClassIOptions]);


	const generateTransportationModeDefinitionExpression = () => {
		const terms = Array.from(selectedTransportationModeOptions)
			.filter(field => field !== 'Truck') // Not applying Truck filter as requested by Watco
			.map(field => `(IB_${field} = 'P' OR OB_${field} = 'P')`);
		const expression = terms.join(" AND ");
		setTransportationModeDefinitionExpression(expression);
	}
	useEffect(generateTransportationModeDefinitionExpression, [selectedTransportationModeOptions]);


	const generateStorageDefinitionExpression = () => {
		const terms = Array.from(selectedStorageOptions).map(field => `${field} = 'P'`);
		const expression = terms.join(" AND ");
		setStorageDefinitionExpression(expression);
	}
	useEffect(generateStorageDefinitionExpression, [selectedStorageOptions]);


	const generateCommodityDefinitionExpression = () => {
		const reducedOptions = Array.from(selectedCommodityOptions).filter(field => field !== "Break_Bulk" && field !== "Project_Cargo")
		const terms = reducedOptions.map(field => `${field} = 'P'`);

		if (selectedCommodityOptions.has("Break_Bulk") || selectedCommodityOptions.has("Project_Cargo")) {
			terms.push("(Break_Bulk = 'P' OR Project_Cargo = 'P')")
		}
		const expression = terms.join(" AND ");
		setCommodityDefinitionExpression(expression);
	}
	useEffect(generateCommodityDefinitionExpression, [selectedCommodityOptions]);


	const applyFilters = () => {
		if (transloadLayer.current) {
			const terms = [];

			if (classIDefinitionExpression) {
				terms.push(classIDefinitionExpression);
			}

			if (transportationModeDefinitionExpression) {
				terms.push(transportationModeDefinitionExpression);
			}

			if (storageDefinitionExpression) {
				terms.push(storageDefinitionExpression);
			}

			if (commodityDefinitionExpression) {
				terms.push(commodityDefinitionExpression);
			}

			const definitionExpression = terms.map(term => `(${term})`).join(" AND ");
			transloadLayer.current.definitionExpression = definitionExpression;
		}
	}
	useEffect(applyFilters, [
		classIDefinitionExpression,
		transportationModeDefinitionExpression,
		storageDefinitionExpression,
		commodityDefinitionExpression
	]);

	const getFiltersSnapshot = useCallback(() => {
		return {
			state: selectedStates,
			classIRailroad: selectedClassIOptions,
			transportMode: selectedTransportationModeOptions,
			storage: selectedStorageOptions,
			commodity: selectedCommodityOptions
		}
	}, [
		selectedStates,
		selectedClassIOptions,
		selectedTransportationModeOptions,
		selectedStorageOptions,
		selectedCommodityOptions
	])

	let filtersStyle: CSSProperties = {};
	let filterWrapStyle: CSSProperties = {
		flexWrap: "wrap",
		flexShrink: "1"
	};
	let filterOverflowStyle: CSSProperties = {
		justifyContent: "flex-end",
		flexGrow: "1",
		flexShrink: "0"
	};
	let stateOptionsStyle: CSSProperties = {};
	if (props.isSmallScreen) {
		filtersStyle = {
			flexDirection: 'column',
			alignItems: 'start',
			padding: '16px 25px',
			height: 'calc(100% - 28px)',
			border: 'none',
			overflowY: 'auto',
		};

		filterWrapStyle = {
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: '16px'
		};

		filterOverflowStyle = {
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: '16px'
		};

		stateOptionsStyle = {
			height: 'calc(100vh - 265px)',
		};
	}

	if (!props.visible) {
		filtersStyle.display = 'none';
	}

	return (
		<div className="transloading-filters" style={filtersStyle}>
			<span className="title">Filters:</span>
			<div className="filter-wrapper" style={filterWrapStyle}>
				<Dropdown isSmallScreen={props.isSmallScreen}
					buttonClass={selectedStates.size > 0 ? 'bg-digital-blue no-border' : ''}
					label='State or Province'>
					<div className="clear-button-container">
						<button onClick={clearStateFilters}>Clear State Filters</button>
					</div>
					<div style={stateOptionsStyle} className="state-options">
						{props.stateOptions.map(option =>
							<div className="filter-option" key={option.value}>
								<input type="checkbox"
									id={`filter-state-${option.value}`}
									onChange={onStateCheckboxChange}
									checked={selectedStates.has(option.value)}
									value={option.value} />
								<label htmlFor={`filter-state-${option.value}`}>{option.label}</label>
							</div>
						)}
					</div>
				</Dropdown>
				<Dropdown isSmallScreen={props.isSmallScreen}
					buttonClass={selectedClassIOptions.size > 0 ? 'bg-digital-blue no-border' : ''}
					label='Class I Railroad'>
					<div className="clear-button-container">
						<button onClick={selectAllClassIFilters}>Select All</button>
						<button onClick={clearClassIFilters}>Clear Class I Filters</button>
					</div>
					<div className="class1-options">
						{props.classIOptions.map(option =>
							<div className="filter-option" key={option}>
								<input type="checkbox"
									id={`filter-class1-${option}`}
									onChange={onClassICheckboxChange}
									checked={selectedClassIOptions.has(option)}
									value={option} />
								<label htmlFor={`filter-class1-${option}`}>{option}</label>
							</div>
						)}
					</div>
				</Dropdown>
				<Dropdown
					isSmallScreen={props.isSmallScreen}
					buttonClass={radiusCenterPoint ? 'bg-digital-blue no-border' : ''}
					label='Distance'>
					<Radius 
						mapView={props.view}
						sketchLayer={radiusSketchLayer}
						highlightSymbol={highlightSymbol}
						centerPoint={radiusCenterPoint}
						setCenterPoint={setRadiusCenterPoint}
						setRadiusGeometry={setRadiusFilterGeometry}
						searchText={radiusSearchText}
						setSearchText={setRadiusSearchText}
						showMap={props.showMap}
					/>
				</Dropdown>
				<Dropdown
					isSmallScreen={props.isSmallScreen}
					buttonClass={selectedTransportationModeOptions.size > 0 ? 'bg-digital-blue no-border' : ''}
					label='Transport Mode'>
					<div className="clear-button-container">
						<button onClick={clearTransportationModeFilters}>Clear Filter</button>
					</div>
					<div className="transportation-mode-container">
						{TransportationModeOptions.map((option, index) =>
							<div className="filter-option" key={index}>
								<input type="checkbox"
									id={`transportation-mode-${option.field}`}
									onChange={onTransportationModeCheckboxChange}
									checked={selectedTransportationModeOptions.has(`${option.field}`)}
									value={`${option.field}`} />
								<label htmlFor={`transportation-mode-${option.field}`}>{option.label} Served</label>
							</div>
						)}
					</div>
				</Dropdown>
				<Dropdown
					isSmallScreen={props.isSmallScreen}
					buttonClass={selectedStorageOptions.size > 0 ? 'bg-digital-blue no-border' : ''}
					label='Storage'>
					<div className="clear-button-container">
						<button onClick={clearStorageFilters}>Clear Filter</button>
					</div>
					<div className="storage-container">
						{StorageOptions.map((option, index) =>
							<div className="filter-option" key={index}>
								<input type="checkbox"
									id={`storage-filter-${option.field}`}
									onChange={onStorageCheckboxChange}
									checked={selectedStorageOptions.has(option.field)}
									value={option.field} />
								<label htmlFor={`storage-filter-${option.field}`}>{option.label}</label>
							</div>
						)}
					</div>
				</Dropdown>
				<Dropdown
					isSmallScreen={props.isSmallScreen}
					buttonClass={selectedCommodityOptions.size > 0 ? 'bg-digital-blue no-border' : ''}
					label='Commodity'>
					<div className="clear-button-container">
						<button onClick={clearCommodityFilters}>Clear Filter</button>
					</div>
					<div className="commodity-container">
						{CommodityOptions.map((option, index) =>
							<div className="filter-option" key={index}>
								<input type="checkbox"
									id={`commodity-filter-${option.field}`}
									onChange={onCommodityCheckboxChange}
									checked={selectedCommodityOptions.has(option.field)}
									value={option.field} />
								<label htmlFor={`commodity-filter-${option.field}`}>{option.label}</label>
							</div>
						)}
					</div>
				</Dropdown>
			</div>
			<div className="filter-wrapper" style={filterOverflowStyle}>
				<button className="bg-orange no-border" onClick={clearAllFilters}>Clear All Filters</button>
				<div className="checkbox-container">
					<input type="checkbox" id="filter-by-map-extent-checkbox"
						checked={props.filterByMapExtent}
						onChange={() => { props.toggleFilterByMapExtent() }} />
					<label htmlFor="filter-by-map-extent-checkbox">Filter by Map Extent</label>
				</div>
				<button className="bg-gray-6 no-border" onClick={reset}>Reset</button>
			</div>
		</div>
	)
}

export default forwardRef(Filters);